import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import type {
  PlaceSuggestion,
  PlaceSuggestionStatus,
  PlaceSuggestionStatusCounts,
  RegisterPlaceRequest,
} from '../api/map'
import { toastUtil } from '../../utils/toast'
import AdminSidebar from '../components/AdminSidebar'
import AdminBaseModal from '../modals/AdminBaseModal'
import AdminPlaceSuggestionDetailModal from '../models/AdminPlaceSuggestionDetailModal'
import AdminPlaceSuggestionStatusBadge from '../components/AdminPlaceSuggestionStatusBadge'
import AdminKakaoPlaceForm from '../components/AdminKakaoPlaceForm'

/** 상태 필터 탭. 빈 문자열은 '전체'. */
type StatusFilter = '' | PlaceSuggestionStatus

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: 'pending', label: '미처리' },
  { value: 'registered', label: '등록 완료' },
  { value: 'rejected', label: '반려' },
  { value: '', label: '전체' },
]

const AdminPlaceSuggestions: React.FC = () => {
  const navigate = useNavigate()
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<PlaceSuggestion | null>(null)
  // 기본은 '미처리' — 관리자가 할 일부터 보게 한다.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending')
  const [statusCounts, setStatusCounts] = useState<PlaceSuggestionStatusCounts>({
    pending: 0,
    registered: 0,
    rejected: 0,
  })
  const [isDirectAddOpen, setIsDirectAddOpen] = useState(false)

  const fetchSuggestions = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await adminApi.getPlaceSuggestions({
        page: currentPage,
        per_page: 20,
        search: search || undefined,
        status: statusFilter || undefined,
      })
      setSuggestions(response.data)
      setLastPage(response.last_page)
      setTotal(response.total)
      if (response.status_counts) {
        setStatusCounts(response.status_counts)
      }
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        toastUtil.error('장소 제안 목록을 불러오는데 실패했습니다')
      }
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, search, statusFilter, navigate])

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token')
    if (!adminToken) {
      navigate('/admin/login')
      return
    }
    fetchSuggestions()
  }, [navigate, fetchSuggestions])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchSuggestions()
  }

  const handleStatusFilterChange = (value: StatusFilter) => {
    setStatusFilter(value)
    setCurrentPage(1)
  }

  const handleViewDetail = (suggestion: PlaceSuggestion) => {
    setSelectedSuggestion(suggestion)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedSuggestion(null)
  }

  /**
   * 등록·반려가 끝나면 목록을 다시 읽는다.
   * 현재 탭이 '미처리'면 처리된 건은 목록에서 빠져야 하므로 서버 기준으로 새로 받는다.
   */
  const handleProcessed = () => {
    fetchSuggestions()
  }

  const handleDirectRegister = async (data: RegisterPlaceRequest) => {
    try {
      const result = await adminApi.createPlaceFromLink(data)
      toastUtil.success(`${result.data.name} — ${result.message}`)
      setIsDirectAddOpen(false)
    } catch (error: any) {
      toastUtil.error(error?.response?.data?.message || '등록에 실패했습니다')
      throw error
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const countFor = (value: StatusFilter) => {
    if (value === '') {
      return (
        statusCounts.pending + statusCounts.registered + statusCounts.rejected
      )
    }
    return statusCounts[value]
  }

  return (
    <div className="min-h-screen bg-backgroundColorBg2 flex">
      <AdminSidebar />

      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-brandPrimary-10% sticky top-0 z-10">
          <div className="px-24px py-16px flex items-center justify-end">
            <button
              onClick={() => {
                localStorage.removeItem('admin_token')
                navigate('/admin/login')
              }}
              className="text-style-button2 text-textColorText2 hover:text-brown-80%-main transition-colors"
            >
              로그아웃
            </button>
          </div>
        </header>

        <main className="flex-1 px-24px py-32px">
          <div className="bg-white rounded-16px shadow-sm p-32px">
            <div className="flex items-center justify-between mb-24px">
              <h2 className="text-style-title2 text-textColorText2">
                장소 제안 관리
              </h2>
              <div className="flex items-center gap-16px">
                <div className="text-style-body2 text-textColorText2">
                  총 {total.toLocaleString()}개
                </div>
                <button
                  onClick={() => setIsDirectAddOpen(true)}
                  className="py-10px px-20px bg-4B3621-80% text-white rounded-8px text-style-button2 hover:bg-4B3621-80%-70% transition-colors"
                >
                  장소 직접 등록
                </button>
              </div>
            </div>

            {/* 상태 탭 */}
            <div className="flex gap-8px mb-20px">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value || 'all'}
                  onClick={() => handleStatusFilterChange(tab.value)}
                  className={`
                    py-8px
                    px-16px
                    rounded-8px
                    text-style-button2
                    border
                    transition-colors
                    ${
                      statusFilter === tab.value
                        ? 'bg-4B3621-80% text-white border-transparent'
                        : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                    }
                  `}
                >
                  {tab.label} {countFor(tab.value)}
                </button>
              ))}
            </div>

            <form onSubmit={handleSearch} className="mb-24px">
              <div className="flex gap-12px">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="상호명, 지역, 사용자명으로 검색"
                  className={`
                  flex-1
                  py-12px
                  px-16px
                  border
                  border-brandPrimary-20%
                  rounded-8px
                  text-style-body1
                  text-textColorText2
                  bg-transparent
                  focus:outline-none
                  focus:border-brandPrimary
                  placeholder:text-textColorText2
                `}
                />
                <button
                  type="submit"
                  className="py-12px px-24px bg-4B3621-80% text-white rounded-8px text-style-button2 hover:bg-4B3621-80%-70% transition-colors"
                >
                  검색
                </button>
              </div>
            </form>

            {isLoading ? (
              <div className="flex justify-center items-center py-80px">
                <div className="text-style-body2 text-textColorText2">
                  로딩 중...
                </div>
              </div>
            ) : suggestions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-80px">
                <div className="text-style-body2 text-textColorText2 mb-8px">
                  {statusFilter === 'pending'
                    ? '처리할 제안이 없습니다.'
                    : '장소 제안이 없습니다.'}
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-brandPrimary-10%">
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          ID
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          상태
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          상호명
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          지역
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          등록된 장소
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          사용자
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          작성일
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          관리
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {suggestions.map((suggestion) => (
                        <tr
                          key={suggestion.id}
                          className="border-b border-brandPrimary-5% hover:bg-backgroundColorBg2 transition-colors"
                        >
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {suggestion.id}
                          </td>
                          <td className="py-12px px-16px">
                            <AdminPlaceSuggestionStatusBadge
                              status={suggestion.status}
                            />
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            <div className="text-ellipsis-1">
                              {suggestion.store_name}
                            </div>
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            <div className="text-ellipsis-1">
                              {suggestion.region}
                            </div>
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            <div className="text-ellipsis-1">
                              {suggestion.places && suggestion.places.length > 0
                                ? suggestion.places
                                    .map((place) => place.name)
                                    .join(', ')
                                : '-'}
                            </div>
                            {/* 상호가 여러 곳이면 몇 곳을 등록했는지 함께 보여준다 */}
                            {suggestion.places &&
                              suggestion.places.length > 1 && (
                                <div className="text-style-labelS text-textColorText3 mt-2px">
                                  {suggestion.places.length}곳
                                </div>
                              )}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {suggestion.user
                              ? `${suggestion.user.nickname} (${suggestion.user.username})`
                              : '알 수 없음'}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {formatDate(suggestion.created_at)}
                          </td>
                          <td className="py-12px px-16px">
                            <button
                              onClick={() => handleViewDetail(suggestion)}
                              className={`
                                py-8px
                                px-16px
                                bg-4B3621-80%
                                text-white
                                rounded-8px
                                text-style-button2
                                hover:bg-4B3621-80%-70%
                                transition-colors
                                whitespace-nowrap
                              `}
                            >
                              {suggestion.status === 'pending'
                                ? '처리하기'
                                : '상세보기'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {lastPage > 1 && (
                  <div className="flex items-center justify-center gap-8px mt-32px">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                      className={`
                      py-8px
                      px-16px
                      rounded-8px
                      text-style-button2
                      border
                      ${
                        currentPage === 1
                          ? 'border-brandPrimary-10% text-textColorText3 cursor-not-allowed'
                          : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                      }
                    `}
                    >
                      이전
                    </button>
                    <div className="text-style-body2 text-textColorText2">
                      {currentPage} / {lastPage}
                    </div>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(lastPage, prev + 1))
                      }
                      disabled={currentPage === lastPage}
                      className={`
                      py-8px
                      px-16px
                      rounded-8px
                      text-style-button2
                      border
                      ${
                        currentPage === lastPage
                          ? 'border-brandPrimary-10% text-textColorText3 cursor-not-allowed'
                          : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                      }
                    `}
                    >
                      다음
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* 제안 상세·처리 모달 */}
      <AdminBaseModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="장소 제안 처리"
        maxWidth="max-w-[640px]"
        overlayOpacity="bg-opacity-50"
      >
        {selectedSuggestion && (
          <AdminPlaceSuggestionDetailModal
            suggestion={selectedSuggestion}
            onClose={handleCloseModal}
            formatDate={formatDate}
            onProcessed={handleProcessed}
          />
        )}
      </AdminBaseModal>

      {/* 제안 없이 직접 등록 */}
      <AdminBaseModal
        isOpen={isDirectAddOpen}
        onClose={() => setIsDirectAddOpen(false)}
        title="장소 직접 등록"
        maxWidth="max-w-[640px]"
        overlayOpacity="bg-opacity-50"
      >
        <div className="p-24px">
          <AdminKakaoPlaceForm
            onSubmit={handleDirectRegister}
            submitLabel="장소 등록"
          />
        </div>
      </AdminBaseModal>
    </div>
  )
}

export default AdminPlaceSuggestions
